/*
 * Copyright (c) 2023 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { initialContext, ContextUpdater, RequestNavigateContext } from "./ContextProvider";
import { constants } from "../util/constants/Constants";
import OverlaySpinner from "../elements/OverlaySpinner";
import debug from "../util/constants/Debug";

export const makeRequest = async ({
  server = "radius",
  route,
  method,
  token,
  payload,
  form,
  asyncHandler = null,
}) => {
  // prepare request url
  let urlBase;
  switch (server) {
    case "radius": urlBase = constants.radiusEndpointBase; break;
    case "remoteManager": urlBase = constants.remoteManagerEndpointBase; break;
    default: debug.error("Invalid server + " + server); return null;
  }
  const reqUrl = urlBase + route;

  // headers
  const headerObj = {
    ...(payload != null ? { "Content-Type": "application/json" } : {}),
    ...(token != null ? {
      "Authorization": `Bearer ${token}`,
      "Access-Control-Request-Headers": "Authorization, Content-Type"
    } : {})
  };
  const headers = new Headers(headerObj);

  // body
  let body = undefined;
  if (payload) {
    body = JSON.stringify(payload);
  }
  else if (form) {
    body = form;
  }

  const requestParameters = {
    method: method ?? "POST",
    headers: headers,
    mode: "cors",
    body: body
  };

  try {
    // make request
    const response = await fetch(reqUrl, requestParameters);

    // check content type
    const contentType = response.headers.get("content-type");

    // parse response based on type
    let resp;
    if (contentType == null || contentType.includes("application/json")) {
      const text = await response.text();
      resp = JSON.parse(text);
    }
    else if (
      contentType.includes("text/plain") ||
      contentType.includes("text/html") ||
      contentType.includes("application/x-x509-ca-cert") ||
      contentType.includes("application/octet-stream"))
    {
      const text = await response.text();
      resp = text;
    }
    else if (contentType.includes("application/zip")) {
      const blob = await response.blob();
      resp = blob;
    }
    else {
      debug.warn("Response content type not supported: " + contentType);
    }

    // session expired
    if (resp.status === "session_expired") {
      asyncHandler?.({ sessionExpired: resp.message });
      return { sessionExpired: resp.message };
    }

    // request failed
    if (!response.ok || resp.status === "error") {
      // parse out explicit error
      let error = resp.error;

      // write implicit error if not explicit error is provided
      if (error == null) {
        switch (response.status) {
          case 401:
          case 403:
            error = "Not authorized";
            break;
          default:
            debug.log('Sending Error to server');
            error = "Error communicating with server";
        }
      }

      // return error
      debug.warn("Request error: " + error);
      asyncHandler?.({ error: error, response: resp });
      return { error: error, response: resp };
    }

    // successful request
    asyncHandler?.({ response: resp });
    return { response: resp };
  } catch (error) {
    debug.warn(`Request error caught: "${error}"`);
    asyncHandler?.({ error: "Invalid server response" });
    return { error: "Invalid server response" };
  }
};

const RequestNavigator = () => {
  // context
  const updateContexts = useContext(ContextUpdater);
  const requestContext = useContext(RequestNavigateContext);

  // state
  const [navigateTo, setNavigateTo] = useState(null);
  const [forceLogout, setForceLogout] = useState(null);

  // navigation
  const navigate = useNavigate();

  // make requests if needed
  useEffect(() => {
    if (requestContext.pending) {
      return;
    }

    const makeRequests = async (requests, retries) => {
      const requestResults = Array.from({ length: requests.count }, () => ({}));
      const requestsComplete = Array.from({ length: requests.count }, () => (false));

      try {
        // request attempts
        const iterations = retries + 1;
        for (let i = 0; i < iterations; i++) {
          // error flag
          let sessionExpired = null;
          let error = false;

          // make requests
          const pendingRequests = requests.map((item, index) => {
            if (requestsComplete[index]) {
              return null;
            }
            return makeRequest(item);
          });
          const results = await Promise.all(pendingRequests);
          
          // handle each response
          requests.every((item, index) => {
            // if async, request handled by makeRequest
            if (item.asyncHandler) {
              return true;
            }

            // wait for request
            const result = item.handler(results[index]);

            // handle result
            requestResults[index] = result;

            if (result?.sessionExpired) {
              sessionExpired = result.sessionExpired;
              return false;
            }
            
            if (!result?.error) {
              requestsComplete[index] = true;
            } else {
              debug.warn(`[Try ${i + 1}] Request to "${item.route}" failed.`);
              error = true;
            }

            return true;
          });
  
          // handle session expire
          if (sessionExpired) {
            setForceLogout(sessionExpired);
            return;
          }

          // retry if any requests failed
          if (!error) {
            // make aggregate result
            const aggregateResult = requestResults.reduce((result, obj) => {
              for (const key in obj) {
                if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
                  // Merge nested objects
                  result[key] = { ...result[key], ...obj[key] };
                } else {
                  // Merge regular properties
                  result[key] = obj[key];
                }
              }
              return result;
            }, {});
    
            // set navigation route
            const navigateRoute = requestContext.handleNavigate?.(aggregateResult);
            setNavigateTo(navigateRoute);

            // update global contexts
            updateContexts(aggregateResult);
            return;
          }
        }
      } catch (error) {
        debug.error(`Error caught in RequestNavigator makeRequests(): ${error.stack}`);
      } finally {
        // log results
        debug.log('Request Results');
        debug.log(requestResults.length === 1 ? requestResults[0] : requestResults);

        // handle completion
        requestContext.handleCompletion?.(requestResults);

        // update request context
        requestContext.updateState({
          handleNavigate: null,
          handleCompletion: null,
          showLoading: false,
          requests: [],
          retries: null,
          pending: false,
          async: false,
        });
      }
    };

    const makeAsyncRequests = async (requests, retries) => {
      const requestResults = Array.from({ length: requests.count }, () => ({}));

      try {
        // request attempts
        const iterations = retries + 1;
        // error flag
        let sessionExpired = null;
        let error = false;
        let resultsindex = 0;
        for (let i = 0; i < iterations; i++) {
          // make requests
          for (const r of requests) {
            const asyncRequest = makeRequest({...r, asyncHandler: (asyncResponse) => {
              const result = r.handler(asyncResponse);
              if (result?.sessionExpired) {
                sessionExpired = result.sessionExpired;
                return false;
              }
              
              // If no error, handle
              if (!result?.error) {
                // set navigation route
                const navigateRoute = requestContext.handleNavigate?.(result);
                setNavigateTo(navigateRoute);
    
                // update global contexts
                updateContexts(result);
              } else {
                debug.warn(`[Try ${i + 1}] Request to "${r.route}" failed.`);
                error = true;
              }
            }});

            const results = await asyncRequest;
            requestResults[resultsindex] = results;
            resultsindex++;
          };

          // handle session expire
          if (sessionExpired) {
            setForceLogout(sessionExpired);
            return;
          }

          // retry if any requests failed
          if (!error) {
            return;
          }
        }
      } catch (error) {
        debug.error(`Error caught in RequestNavigator makeRequests(): ${error.stack}`);
      } finally {
        // log results
        debug.log('Request Results');
        debug.log(requestResults.length === 1 ? requestResults[0] : requestResults);

        // handle completion
        requestContext.handleCompletion?.(requestResults);

        // update request context
        requestContext.updateState({
          handleNavigate: null,
          handleCompletion: null,
          showLoading: false,
          requests: [],
          retries: null,
          pending: false,
          async: false,
        });
      }
    };

    if (requestContext.requests.length > 0) {
      if (requestContext.async) {
        makeAsyncRequests(requestContext.requests, requestContext.retries ?? 0);
        requestContext.updateState({async: false, pending: true})
      } else {
        makeRequests(requestContext.requests, requestContext.retries ?? 0);
      }
    }
  }, [requestContext, updateContexts, navigate]);

  // navigate if needed
  useEffect(() => {
    if (navigateTo != null) {
      // navigate
      navigate(navigateTo);
      
      // update state
      setNavigateTo(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigateTo]);

  // force logout if needed
  useEffect(() => {
    const forceLogoutMessage = forceLogout;
    if (forceLogoutMessage) {
      setForceLogout(false);

      // clear contexts
      updateContexts({ ...initialContext });
      updateContexts({ account: { loginMessage: forceLogoutMessage } });

      // clear token from session storage
      sessionStorage.removeItem(constants.storageTokenKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceLogout])

  return <OverlaySpinner show={requestContext.showLoading} />;
};

export default RequestNavigator;
